import * as React from 'react';
import {times, isBoolean, isObject, isString, clamp} from 'lodash';
import {CheckMedium as Checkmark} from '@packages/systems/ui/icons';
import {emptyObject, emptyArray} from '@packages/utilities/fp/utils';
import {contain} from '@packages/utilities/box';
import {translate} from '@packages/utilities/inline-style-utils';
import {getAutomationId, DATA_AUTOMATION_ID} from './constants/AutomationUtils';
import {useTheme, emotionStyled} from '@packages/utilities/styled';
import {safeCloneElement} from '@packages/utilities/component';
import {getHex} from '@packages/systems/core/utils/ColorUtils';
import type {Theme} from '@packages/utilities/styled';
import type {Box} from '@packages/utilities/box';
import type {CSSProperties} from '@packages/utilities/css-properties-type';
import {Layer, useViewport} from '@packages/systems/ui/overlay';
import ScrollView from './ScrollView';
import * as Menu from './Menu';

import {
  getValue,
  getLabel,
  menuHeight,
  selectedTop,
  groups,
  groupHeight,
  indexInGroup,
} from './logic/dropdown';

export {Option, Group} from './Dropdown';

// @ts-expect-error - TS7006 - Parameter 'style' implicitly has an 'any' type.
const toHex = (name: string, style) => {
  return getHex({
    r: style[name + 'R'],
    g: style[name + 'G'],
    b: style[name + 'B'],
    a: style[name + 'A'],
  });
};

const checkmarkWidth = Checkmark(emptyObject).props.width;

type ItemProps = {
  top: number;
  left: number;
  width: number;
  height: number;
  menuOpen: boolean;
  selectable: boolean;
  preselected?: any;
  header: boolean;
  style: CSSProperties;
  disabled: boolean;
  value: any;
  onPreselect: (arg1?: any) => void;
  onChange: (arg1?: any) => void;
  selected: boolean;
  ['data-automation-id']: string;
  label: string;
  singleLine?: boolean;
  onMouseDown?: (event: React.MouseEvent<HTMLElement>) => void;
};

const _Item = (props: ItemProps) => {
  const onMouseEnter = () => props.onPreselect(props.value);
  const onClick = () => props.onChange(props.value);
  const selectable = isBoolean(props.selectable) ? props.selectable : true;
  const disabled = props.disabled || false;

  // props.label could either be a react component or a simple string/object
  // if it's a react component, we want to provide the menuOpen flag via safeCloneElement
  const {label: itemLabel} = props;
  const label =
    isObject(itemLabel) || isString(itemLabel)
      ? itemLabel
      : safeCloneElement(
          // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'never'.
          {...itemLabel.props, menuOpen: props.menuOpen},
          itemLabel
        );

  const hasNoValue = props.value === null || typeof props.value === 'undefined';
  /**
   * Sometimes, Selects are setup where the last (Grouped) item is a <Note />.
   * These <Option /> that contain Notes are setup so that they're disabled
   * and not selectable.
   *
   * We have to interpret if an Option is a Note... or if it's
   * a disabled, non-selectable item. The following logic is used to determine
   * just that.
   *
   * isNoteItem is a boolean used just for styling for this specific case.
   *
   * @see https://webflow.atlassian.net/browse/PDTY-6345
   *
   * 2023-09-22
   */
  const isNoteItem = disabled && selectable === false && hasNoValue;

  return (
    <Menu.Item
      style={{
        position: 'absolute',
        top: props.top,
        left: props.left,
        width: props.width,
        height: props.height,
        ...props.style,
      }}
      selectable={selectable}
      disabled={disabled}
      selected={props.selected}
      header={props.header}
      isNoteItem={isNoteItem}
      hover={props.preselected}
      onMouseEnter={onMouseEnter}
      onMouseDown={props.onMouseDown}
      // @ts-expect-error - TS2322 - Type '(() => void) | undefined' is not assignable to type '((e: MouseEvent<HTMLElement, MouseEvent>) => Promise<any> | undefined) | undefined'.
      onClick={selectable ? onClick : undefined}
      open={props.menuOpen}
      data-automation-id={props['data-automation-id']}
      singleLine={props.singleLine}
    >
      {label}
    </Menu.Item>
  );
};

const Item = React.memo(_Item);

type OptionGroupProps = {
  width: number;
  startIndex: number;
  endIndex: number;
  menuOpen: boolean;
  selectedValue: any;
  preselectedValue?: any;
  onPreselect: (arg1?: any) => void;
  onChange: (arg1?: any) => void;
  onItemMouseDown?: (event: React.MouseEvent<HTMLElement>) => void;
  group: {
    children: Array<{
      selectable: boolean;
      disabled: boolean;
      style: CSSProperties;
      header: boolean;
      // @ts-expect-error - TS1337 - An index signature parameter type cannot be a literal type or generic type. Consider using a mapped object type instead.
      [key: typeof DATA_AUTOMATION_ID]: string;
      singleLine?: boolean;
    }>;
    hasDivider: boolean;
  };
  divided: boolean;
  indicator: any;
  theme: Theme;
};

const _OptionGroup = ({
  theme,
  width,
  divided,
  group,
  startIndex,
  endIndex,
  menuOpen,
  selectedValue,
  preselectedValue,
  indicator,
  onPreselect,
  onChange,
  onItemMouseDown,
}: OptionGroupProps) => (
  <>
    {group.hasDivider && Boolean(divided) && <Menu.GroupDivider />}
    <Menu.Wrapper divided={!group.hasDivider && divided}>
      {times(endIndex - startIndex + 1, (i) => {
        const index = i + startIndex;
        const option = group.children[index];
        const value = getValue(option);

        return (
          <Item
            key={index}
            top={
              index * theme.control.height +
              theme.gutters.x8 / 2 -
              theme.control.borderWidth
            }
            left={0}
            width={width}
            height={theme.control.height}
            menuOpen={menuOpen}
            // @ts-expect-error - TS18048 - 'option' is possibly 'undefined'.
            disabled={option.disabled}
            // @ts-expect-error - TS18048 - 'option' is possibly 'undefined'.
            style={{fontSize: theme.fontSizes.copy, ...option.style}}
            // @ts-expect-error - TS18048 - 'option' is possibly 'undefined'.
            header={option.header}
            value={value}
            label={getLabel(option)}
            selected={selectedValue === value}
            // @ts-expect-error - TS18048 - 'option' is possibly 'undefined'.
            selectable={option.selectable}
            preselected={preselectedValue === value}
            // @ts-expect-error - TS2322 - Type '{ key: number; top: number; left: number; width: number; height: number; menuOpen: boolean; disabled: boolean; style: { accentColor?: unknown; alignContent?: unknown; alignItems?: unknown; alignSelf?: unknown; alignTracks?: unknown; animationDelay?: unknown; ... 779 more ...; "-webkit-user-select"?: unknown; }; ... ...' is not assignable to type 'IntrinsicAttributes & ItemProps'.
            indicator={indicator}
            onPreselect={onPreselect}
            onChange={onChange}
            onMouseDown={onItemMouseDown}
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '"data-automation-id"' can't be used to index type '{ selectable: boolean; disabled: boolean; style: ObjMap<StrictCSSProperties, ToMaybeType>; header: boolean; singleLine?: boolean | undefined; }'.
            data-automation-id={getAutomationId(option[DATA_AUTOMATION_ID])}
            // @ts-expect-error - TS18048 - 'option' is possibly 'undefined'.
            singleLine={option.singleLine}
          />
        );
      })}
    </Menu.Wrapper>
  </>
);

const OptionGroup = React.memo(_OptionGroup);

const ignoreMouseScrolling = (evt: WheelEvent) => {
  evt.preventDefault();
  evt.stopPropagation();
};

const MenuCardView = emotionStyled(Menu.Panel)({color: '$textPrimary'});

type ExtractReturnType = <R>(arg1: () => R) => R;
const selectMenu = ({
  ariaLabelledBy,
  viewport,
  children,
  open,
  value,
  preselected,
  theme,
  indicator,
  onPreselect,
  onChange,
  onScroll,
  onRemovePreselection,
  onClose,
  onItemMouseDown,
  style,
  'data-automation-id': dataAutomationId,
}: {
  open: boolean;
  value: any;
  preselected?: any;
  theme: Theme;
  onPreselect: (arg1?: any) => void;
  onChange: (arg1?: any) => void;
  onScroll?: () => void;
  onRemovePreselection?: () => void;
  onClose: () => void;
  onItemMouseDown?: (event: React.MouseEvent<HTMLElement>) => void;
  ariaLabelledBy?: string;
  indicator?: boolean;
  viewport: ReturnType<ExtractReturnType>;
  children: React.ReactNode;
  style: {
    top: number;
    left: number;
    width: number;
    height: number;
    opacity: number;
    menuTop: number;
    borderRadius: number;
    shadowOpacity: number;
    borderOpacity: number;
    leftOffset: number;
    topOffset: number;
    menuWidth: number;
    menuLeft: number;
    menuHeight: number;
  };
  ['data-automation-id']?: string;
}) => {
  const scrollTop = -style.menuTop;
  // @ts-expect-error - TS18048 - 'checkmarkWidth' is possibly 'undefined'. | TS2365 - Operator '+' cannot be applied to types 'string | number' and 'number'.
  const menuLeft = checkmarkWidth + theme.gutters.normal;

  return (
    <Layer>
      <div style={{pointerEvents: open ? undefined : 'none'}}>
        {/* TODO: fix jsx-a11y lint error */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          // @ts-expect-error - TS2698 - Spread types may only be created from object types.
          style={{...viewport, position: 'absolute'}}
          onMouseEnter={onRemovePreselection}
          onClick={onClose}
          // @ts-expect-error - TS2322 - Type '(evt: WheelEvent) => void' is not assignable to type 'WheelEventHandler<HTMLDivElement>'.
          onWheel={ignoreMouseScrolling}
        />
        <MenuCardView
          data-automation-id={dataAutomationId}
          aria-labelledby={ariaLabelledBy}
          role="listbox"
          style={{
            top: style.top,
            left: style.left - menuLeft,
            width: style.width + menuLeft,
            height: style.height,
            opacity: style.opacity,
            backgroundColor: toHex('backgroundColor', style),
            borderColor: toHex('borderColor', style),
            borderRadius: style.borderRadius,
            shadowOpacity: style.shadowOpacity / 100,
            borderOpacity: style.borderOpacity / 100,
            transform: translate(style.leftOffset, style.topOffset),
          }}
          data-legacy-ds="SelectMenu"
        >
          <ScrollView
            showShadows={false}
            width={style.width + menuLeft}
            height={style.height}
            scrollWidth={style.width}
            scrollHeight={style.menuHeight}
            scrollTop={scrollTop}
            scrollLeft={0}
            onScroll={onScroll}
          >
            {
              groups(children).reduce(
                // @ts-expect-error - TS7006 - Parameter 'result' implicitly has an 'any' type. | TS7006 - Parameter 'group' implicitly has an 'any' type. | TS7006 - Parameter 'groupIndex' implicitly has an 'any' type.
                (result, group, groupIndex) => {
                  const height = groupHeight(theme)(group);
                  const topDelta = result.top - result.current;

                  // Out of view
                  if (topDelta > height || -topDelta > result.height) {
                    return {
                      groups: result.groups,
                      current: result.current + height,
                      top: result.top,
                      height: result.height,
                    };
                  }

                  const startIndex = Math.max(0, indexInGroup(theme)(topDelta));
                  const endIndex = Math.min(
                    group.children.length - 1,
                    indexInGroup(theme)(topDelta + result.height)
                  );

                  return {
                    groups: result.groups.concat(
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: style.menuWidth,
                          height,
                          transform: translate(
                            style.menuLeft,
                            style.menuTop + result.current
                          ),
                        }}
                        key={groupIndex}
                      >
                        <OptionGroup
                          theme={theme}
                          width={style.menuWidth}
                          divided={groupIndex}
                          group={group}
                          startIndex={startIndex}
                          endIndex={endIndex}
                          menuOpen={open}
                          selectedValue={value}
                          preselectedValue={preselected}
                          indicator={indicator}
                          onPreselect={onPreselect}
                          onItemMouseDown={onItemMouseDown}
                          onChange={onChange}
                        />
                      </div>
                    ),
                    current: result.current + height,
                    top: result.top,
                    height: result.height,
                  };
                },
                {
                  groups: emptyArray,
                  current: 0,
                  top: scrollTop > 0 ? scrollTop : 0,
                  height: style.height,
                }
              ).groups
            }
          </ScrollView>
        </MenuCardView>
      </div>
    </Layer>
  );
};

type DropdownProps = {
  open: boolean;
  width: number;
  value: any;
  preselected?: any;
  children: React.ReactNode;
  onPreselect: (arg1?: any) => void;
  onChange: (arg1?: any) => void;
  onClose: () => void;
  onRemovePreselection?: () => void;
  onScroll?: () => void;
  onItemMouseDown?: (event: React.MouseEvent<HTMLElement>) => void;
  hasScrolled?: boolean;
  scrollView?: {
    scrollTop: number;
    scrollLeft: number;
  };
  menuOffset: {
    top: number;
    left: number;
  };
  ['aria-labelledby']?: string;
  indicator?: boolean;
  anchor: Box;
  centerSelectedItem?: boolean;
  ['data-automation-id']?: string;
};

/**
 * @deprecated use `Menu` (for triggering an action) or `Select` (for choosing an option value) from `@packages/systems/spring` instead
 */
export const Dropdown = (props: DropdownProps) => {
  const theme = useTheme();
  const viewport = useViewport();

  const {dropdown, gutters, card} = theme;
  const {
    width,
    anchor,
    hasScrolled,
    scrollView = {scrollTop: 0, scrollLeft: 0},
    menuOffset,
    open,
    value,
    preselected,
    indicator,
    children,
    onPreselect,
    onChange,
    onScroll,
    onRemovePreselection,
    onClose,
    onItemMouseDown,
    'aria-labelledby': ariaLabelledBy,
    centerSelectedItem = true,
    'data-automation-id': dataAutomationId,
  } = props;

  if (open === false) return null;

  const cardBorderRadius = card.borderRadius;
  const controlBorderWidth = theme.Select.controlBorderWidth;
  const doubleControlBorderWidth = 2 * controlBorderWidth;
  // @ts-expect-error - TS18048 - 'checkmarkWidth' is possibly 'undefined'.
  const menuCheckmark = -checkmarkWidth - gutters.x8;
  const menuWidth =
    (open ? width : anchor.width + menuOffset.left) -
    menuCheckmark -
    doubleControlBorderWidth;
  const outerHeight = menuHeight(theme)(children);
  const menuTop = centerSelectedItem
    ? -selectedTop(theme)(value || 0, children)
    : 0;

  const box = contain(viewport)({
    left: anchor.left,
    top: anchor.top + menuTop - menuOffset.top,
    width: open ? width : anchor.width + menuOffset.left,
    height: Math.min(outerHeight, viewport.height),
  });

  // Unless the user scrolled we want to scroll to the selected item.
  const scrollTop = hasScrolled
    ? scrollView.scrollTop
    : outerHeight > viewport.height
      ? clamp(-menuTop + box.top - anchor.top, 0, outerHeight - viewport.height)
      : 0;

  return selectMenu({
    style: {
      width: box.width,
      height: box.height,
      top: anchor.top,
      left: anchor.left,
      topOffset: box.top - anchor.top,
      leftOffset: box.left - anchor.left,
      // @ts-expect-error - TS2322 - Type '{ width: number; height: number; top: number; left: number; topOffset: number; leftOffset: number; backgroundColor: string; borderColor: string; borderRadius: number; shadowOpacity: number; borderOpacity: number; ... 4 more ...; menuHeight: number; }' is not assignable to type '{ top: number; left: number; width: number; height: number; opacity: number; menuTop: number; borderRadius: number; shadowOpacity: number; borderOpacity: number; leftOffset: number; topOffset: number; menuWidth: number; menuLeft: number; menuHeight: number; }'.
      backgroundColor: dropdown.backgroundColor,
      borderColor: dropdown.backgroundColor,
      borderRadius: cardBorderRadius,
      shadowOpacity: 15,
      borderOpacity: 100,
      menuLeft: 0,
      opacity: 100,
      menuTop: -scrollTop,
      menuWidth,
      menuHeight: outerHeight,
    },
    ariaLabelledBy,
    children,
    theme,
    viewport,
    open,
    value,
    preselected,
    indicator,
    onPreselect,
    onChange,
    onScroll,
    onRemovePreselection,
    onClose,
    onItemMouseDown,
    'data-automation-id': dataAutomationId,
  });
};
